// src/pages/Admin/TradingBotsManagement.tsx
// Full file — apply this to src/pages/Admin/TradingBotsManagement.tsx
// Changes vs original:
//   + FormData has returning_customer_bonus: string
//   + DEFAULT_FORM has returning_customer_bonus: ""
//   + openEdit() maps bot.returning_customer_bonus
//   + buildFormData() appends returning_customer_bonus
//   + BotForm has a new "Returning Customer Bonus" field inside the Referral section
//   + Table shows bonus column
//   + Stats modal shows bonus row
//   + Investment analytics modal shows bonus row
// All other logic is unchanged.

import React, { useState, useEffect } from "react";
import Header from "../../components/Layout/Header";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import api from "../../services/api";
import {
  formatCurrency,
  formatNumber,
  getXrpPrice,
  formatXRP,
} from "../../utils/formatters";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";
import BottomNav from "../../components/Layout/BottomNav";

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading trading bots...",
}) => (
  <div className="w-full h-64 bg-black">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="mt-4 text-gray-400">{message}</p>
    </div>
  </div>
);

interface TradingBot {
  id: number;
  name: string;
  description: string;
  daily_rate: number;
  min_balance: number;
  max_balance: number | null;
  duration_days: number;
  return_capital: boolean;
  max_users: number;
  current_users: number;
  is_active: boolean;
  min_referrals: number;
  referral_bonus: number;
  returning_customer_bonus: number | null;
  image_url: string | null;
  color: string;
  priority: number;
  created_at: string;
  updated_at: string;
  users_count?: number;
  stats?: {
    average_investment?: number;
    min_investment?: number;
    max_investment?: number;
    total_invested?: number;
  };
}

interface FormData {
  name: string;
  description: string;
  daily_rate: string;
  min_balance: string;
  max_balance: string;
  duration_days: string;
  return_capital: boolean;
  max_users: string;
  min_referrals: string;
  referral_bonus: string;
  returning_customer_bonus: string;
  color: string;
  priority: string;
  is_active: boolean;
  image: File | null;
}

const DEFAULT_FORM: FormData = {
  name: "",
  description: "",
  daily_rate: "0.69",
  min_balance: "10",
  max_balance: "1000",
  duration_days: "30",
  return_capital: true,
  max_users: "0",
  min_referrals: "0",
  referral_bonus: "0",
  returning_customer_bonus: "",
  color: "#3B82F6",
  priority: "0",
  is_active: true,
  image: null,
};

const AdminTradingBots: React.FC = () => {
  const [bots, setBots] = useState<TradingBot[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [loading, setLoading] = useState({ bots: true, overall: true });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<TradingBot | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<TradingBot | null>(
    null,
  );
  const [showStatsModal, setShowStatsModal] = useState<TradingBot | null>(null);
  const [showInvestmentModal, setShowInvestmentModal] =
    useState<TradingBot | null>(null);
  const [investmentAnalytics, setInvestmentAnalytics] = useState<any>(null);
  const [xrpPrice] = useState(getXrpPrice());
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadBots();
  }, []);

  const loadBots = async (page = 1) => {
    try {
      setLoading({ bots: true, overall: true });
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await api.get(`/admin/trading-bots?${params}`);
      if (res.data && Array.isArray(res.data.data)) {
        setBots(res.data.data);
        setPagination({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          per_page: res.data.per_page,
          total: res.data.total,
        });
      } else {
        const arr = Array.isArray(res.data) ? res.data : res.data?.bots || [];
        setBots(arr);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: arr.length,
          total: arr.length,
        });
      }
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load trading bots");
      setBots([]);
    } finally {
      setLoading({ bots: false, overall: false });
    }
  };

  const loadInvestmentAnalytics = async (botId: number) => {
    try {
      const res = await api.get(
        `/admin/trading-bots/${botId}/investment-analytics`,
      );
      setInvestmentAnalytics(res.data);
    } catch (e: any) {
      setError("Failed to load investment analytics");
    }
  };

  // Builds the multipart FormData payload including the new bonus field.
  const buildFormData = (fd: FormData): any => {
    const out = new window.FormData() as any;
    out.append("name", fd.name);
    out.append("description", fd.description);
    out.append("daily_rate", fd.daily_rate);
    out.append("min_balance", fd.min_balance);
    out.append("max_balance", fd.max_balance || "");
    out.append("duration_days", fd.duration_days);
    out.append("return_capital", fd.return_capital ? "1" : "0");
    out.append("max_users", fd.max_users);
    out.append("min_referrals", fd.min_referrals);
    out.append("referral_bonus", fd.referral_bonus);
    out.append("returning_customer_bonus", fd.returning_customer_bonus || "");
    out.append("color", fd.color);
    out.append("priority", fd.priority);
    out.append("is_active", fd.is_active ? "1" : "0");
    if (fd.image) out.append("image", fd.image);
    return out;
  };

  const handleCreateBot = async () => {
    try {
      setLoading((p) => ({ ...p, bots: true }));
      await api.post("/admin/trading-bots", buildFormData(formData), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Trading bot created successfully");
      setShowCreateModal(false);
      setFormData(DEFAULT_FORM);
      loadBots();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to create trading bot");
    } finally {
      setLoading((p) => ({ ...p, bots: false }));
    }
  };

  const handleUpdateBot = async () => {
    if (!showEditModal) return;
    try {
      setLoading((p) => ({ ...p, bots: true }));
      await api.post(
        `/admin/trading-bots/${showEditModal.id}`,
        buildFormData(formData),
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSuccess("Trading bot updated successfully");
      setShowEditModal(null);
      setFormData(DEFAULT_FORM);
      loadBots();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to update trading bot");
    } finally {
      setLoading((p) => ({ ...p, bots: false }));
    }
  };

  const handleDeleteBot = async () => {
    if (!showDeleteModal) return;
    try {
      await api.delete(`/admin/trading-bots/${showDeleteModal.id}`);
      setSuccess("Trading bot deleted successfully");
      setShowDeleteModal(null);
      loadBots();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to delete trading bot");
    }
  };

  const handleToggleStatus = async (botId: number, current: boolean) => {
    try {
      await api.patch(`/admin/trading-bots/${botId}/toggle`);
      setSuccess(`Trading bot ${!current ? "activated" : "deactivated"}`);
      loadBots();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to update bot status");
    }
  };

  // Populate form from an existing bot when opening the edit modal.
  const openEdit = (bot: TradingBot) => {
    setShowEditModal(bot);
    setFormData({
      name: bot.name,
      description: bot.description || "",
      daily_rate: bot.daily_rate.toString(),
      min_balance: bot.min_balance.toString(),
      max_balance: bot.max_balance !== null ? bot.max_balance.toString() : "",
      duration_days: bot.duration_days.toString(),
      return_capital: bot.return_capital,
      max_users: bot.max_users.toString(),
      min_referrals: bot.min_referrals.toString(),
      referral_bonus: bot.referral_bonus.toString(),
      returning_customer_bonus:
        bot.returning_customer_bonus != null
          ? bot.returning_customer_bonus.toString()
          : "",
      color: bot.color,
      priority: bot.priority.toString(),
      is_active: bot.is_active,
      image: null,
    });
  };

  const openInvestmentStats = async (bot: TradingBot) => {
    setShowInvestmentModal(bot);
    await loadInvestmentAnalytics(bot.id);
  };

  const set = (key: keyof FormData, value: any) =>
    setFormData((p) => ({ ...p, [key]: value }));

  const BotForm = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Bot Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => set("name", e.target.value)}
            className={globalStyle.input}
            placeholder="Premium AI Bot"
          />
        </div>
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.color}
              onChange={(e) => set("color", e.target.value)}
              className="w-10 h-10 cursor-pointer rounded"
            />
            <input
              type="text"
              value={formData.color}
              onChange={(e) => set("color", e.target.value)}
              className={`${globalStyle.input} flex-1`}
            />
          </div>
        </div>
      </div>

      <div className={globalStyle.formGroup}>
        <label className={globalStyle.label}>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => set("description", e.target.value)}
          className={globalStyle.input}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Daily Rate (%) *</label>
          <input
            type="number"
            step="0.0001"
            min="0.0001"
            max="10"
            value={formData.daily_rate}
            onChange={(e) => set("daily_rate", e.target.value)}
            className={globalStyle.input}
          />
          <p className={`${globalStyle.caption} text-blue-400`}>
            ~
            {formatNumber(
              (Math.pow(1 + parseFloat(formData.daily_rate || "0") / 100, 30) -
                1) *
                100,
              2,
            )}
            % / 30 days
          </p>
        </div>
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Priority</label>
          <select
            value={formData.priority}
            onChange={(e) => set("priority", e.target.value)}
            className={globalStyle.input}
          >
            <option value="0">Normal</option>
            <option value="1">Premium</option>
            <option value="2">VIP</option>
          </select>
        </div>
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Max Users (0=unlimited)</label>
          <input
            type="number"
            min="0"
            value={formData.max_users}
            onChange={(e) => set("max_users", e.target.value)}
            className={globalStyle.input}
          />
          {isEdit && showEditModal && (
            <p className={`${globalStyle.caption} text-gray-400`}>
              Current: {showEditModal.users_count || 0}
            </p>
          )}
        </div>
      </div>

      {/* Investment Settings */}
      <div className="border border-gray-700 rounded-lg p-4 space-y-4">
        <p className="text-sm font-semibold text-white flex items-center gap-2">
          <CurrencyDollarIcon className="w-4 h-4 text-yellow-400" />
          Investment Settings
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              Minimum Investment (XRP) *
            </label>
            <input
              type="number"
              step="0.000001"
              min="0"
              value={formData.min_balance}
              onChange={(e) => set("min_balance", e.target.value)}
              className={globalStyle.input}
            />
            <p className={`${globalStyle.caption} text-gray-400`}>
              ≈{" "}
              {formatCurrency(
                parseFloat(formData.min_balance || "0") * xrpPrice,
              )}{" "}
              USD
            </p>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              Maximum Investment (XRP)
            </label>
            <input
              type="number"
              step="0.000001"
              min={formData.min_balance}
              value={formData.max_balance}
              onChange={(e) => set("max_balance", e.target.value)}
              className={globalStyle.input}
              placeholder="Leave empty for unlimited"
            />
            <p className={`${globalStyle.caption} text-gray-400`}>
              {formData.max_balance
                ? `≈ ${formatCurrency(parseFloat(formData.max_balance) * xrpPrice)} USD`
                : "No upper limit"}
            </p>
          </div>
        </div>
        {parseFloat(formData.min_balance) > 0 && formData.max_balance && (
          <div
            className={`text-sm ${parseFloat(formData.max_balance) > parseFloat(formData.min_balance) ? "text-green-400" : "text-red-400"}`}
          >
            {parseFloat(formData.max_balance) > parseFloat(formData.min_balance)
              ? "✓ Investment range is valid"
              : "✗ Maximum must be greater than minimum"}
          </div>
        )}
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>Duration (Days) *</label>
          <input
            type="number"
            min="1"
            value={formData.duration_days}
            onChange={(e) => set("duration_days", e.target.value)}
            className={globalStyle.input}
          />
          <p className={`${globalStyle.caption} text-gray-400`}>
            Subscription expires after {formData.duration_days} day(s)
          </p>
        </div>
      </div>

      {/* Capital Return Toggle */}
      <div className="border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="return_capital"
            checked={formData.return_capital}
            onChange={(e) => set("return_capital", e.target.checked)}
            className="w-4 h-4 mt-0.5 text-primary border-gray-700 rounded focus:ring-primary bg-gray-800"
          />
          <label
            htmlFor="return_capital"
            className="text-sm text-white cursor-pointer"
          >
            <span className="font-medium">Return Capital on Expiry</span>
            <span className="block text-xs text-gray-400 mt-0.5">
              When enabled, the full invested amount is returned to the user's
              wallet when their subscription expires.
            </span>
          </label>
        </div>
        {formData.return_capital && (
          <div className="mt-3 bg-blue-900/20 border border-blue-800/40 rounded-lg p-3 text-sm text-blue-300">
            💡 Users will receive their full investment amount back after{" "}
            {formData.duration_days} days, plus all earnings accumulated during
            the period.
          </div>
        )}
      </div>

      {/* Referral + Loyalty */}
      <div className="border border-gray-700 rounded-lg p-4 space-y-4">
        <p className="text-sm font-semibold text-white flex items-center gap-2">
          <GiftIcon className="w-4 h-4 text-purple-400" />
          Referral &amp; Loyalty Settings
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Min Referrals Required</label>
            <input
              type="number"
              min="0"
              value={formData.min_referrals}
              onChange={(e) => set("min_referrals", e.target.value)}
              className={globalStyle.input}
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Referral Bonus (%)</label>
            <input
              type="number"
              step="0.0001"
              min="0"
              max="100"
              value={formData.referral_bonus}
              onChange={(e) => set("referral_bonus", e.target.value)}
              className={globalStyle.input}
            />
          </div>
        </div>

        {/* ── Returning Customer Bonus ── */}
        <div className={globalStyle.formGroup}>
          <label className={globalStyle.label}>
            Returning Customer Bonus (XRP)
          </label>
          <input
            type="number"
            step="0.000001"
            min="0"
            value={formData.returning_customer_bonus}
            onChange={(e) => set("returning_customer_bonus", e.target.value)}
            className={globalStyle.input}
            placeholder="Leave empty to disable"
          />
          <p className={`${globalStyle.caption} text-gray-400`}>
            Fixed XRP credited instantly when a user who has previously
            purchased this bot invests again. Leave empty or 0 to disable.
          </p>
        </div>

        {parseFloat(formData.returning_customer_bonus || "0") > 0 && (
          <div className="bg-yellow-900/20 border border-yellow-800/40 rounded-lg p-3 text-sm text-yellow-300">
            🎁 Returning customers will receive{" "}
            <span className="font-semibold">
              {formatXRP(parseFloat(formData.returning_customer_bonus))} XRP
            </span>{" "}
            as a welcome-back bonus each time they re-join this bot.
          </div>
        )}
      </div>

      {/* Image */}
      <div className={globalStyle.formGroup}>
        <label className={globalStyle.label}>Bot Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => set("image", e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:opacity-90"
        />
        {isEdit && showEditModal?.image_url && (
          <p className={`${globalStyle.caption} mt-1 text-gray-400`}>
            Current: ✓ Uploaded
          </p>
        )}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => set("is_active", e.target.checked)}
          className="w-4 h-4 text-primary border-gray-700 rounded focus:ring-primary bg-gray-800"
        />
        <label htmlFor="is_active" className="ml-2 text-sm text-white">
          Active (visible to users)
        </label>
      </div>
    </div>
  );

  return (
    <div className={globalStyle.container}>
      <Header />
      <main className="page-content">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className={globalStyle.h3}>Trading Bots Management</h1>
            <p className={`${globalStyle.body2} text-gray-400`}>
              Configure investment-based AI trading bots • 1 XRP ={" "}
              {formatCurrency(xrpPrice)}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => loadBots()}
              loading={loading.bots}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => {
                setFormData(DEFAULT_FORM);
                setShowCreateModal(true);
              }}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Bot
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadBots(1)}
                placeholder="Search bots..."
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                loadBots(1);
              }}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Button variant="secondary" size="sm" onClick={() => loadBots(1)}>
              Filter
            </Button>
          </div>
        </Card>

        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
            className="mb-6"
          />
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            {
              label: "Total Bots",
              value: pagination.total,
              color: "text-white",
            },
            {
              label: "Active",
              value: bots.filter((b) => b.is_active).length,
              color: "text-green-400",
            },
            {
              label: "Total Users",
              value: bots.reduce((s, b) => s + (b.users_count || 0), 0),
              color: "text-white",
            },
            {
              label: "Total Invested",
              value: formatXRP(
                bots.reduce((s, b) => s + (b.stats?.total_invested || 0), 0),
              ),
              color: "text-yellow-400",
            },
            {
              label: "Avg. Daily Rate",
              value: bots.length
                ? formatNumber(
                    bots.reduce((s, b) => s + (b.daily_rate || 0), 0) /
                      bots.length,
                    4,
                  ) + "%"
                : "0%",
              color: "text-blue-400",
            },
          ].map((s) => (
            <Card key={s.label} className="text-center">
              <p className="text-sm text-gray-400">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </Card>
          ))}
        </div>

        <Card padding="lg" className="mb-6">
          {loading.bots ? (
            <LoadingScreen message="Loading bot data..." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    {[
                      "Bot",
                      "Rate / Duration",
                      "Investment Range",
                      "Users / Total",
                      "Bonus",
                      "Status",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="py-3 px-4 text-left text-sm font-medium text-gray-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bots.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-12 text-center text-gray-400"
                      >
                        No bots found.
                      </td>
                    </tr>
                  ) : (
                    bots.map((bot) => (
                      <tr
                        key={bot.id}
                        className="border-b border-gray-800 hover:bg-gray-800/50"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                              style={{ backgroundColor: `${bot.color}20` }}
                            >
                              <RocketLaunchIcon
                                className="w-5 h-5"
                                style={{ color: bot.color }}
                              />
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {bot.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate max-w-[160px]">
                                {bot.description || "—"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-green-400">
                            {formatNumber(bot.daily_rate, 4)}%
                          </p>
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {bot.duration_days} days
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <ShieldCheckIcon
                              className={`w-3 h-3 ${bot.return_capital ? "text-blue-400" : "text-gray-600"}`}
                            />
                            <span
                              className={`text-xs ${bot.return_capital ? "text-blue-400" : "text-gray-500"}`}
                            >
                              {bot.return_capital
                                ? "Returns capital"
                                : "No return"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-white">
                            {formatXRP(bot.min_balance)} -{" "}
                            {bot.max_balance ? formatXRP(bot.max_balance) : "∞"}{" "}
                            XRP
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatCurrency(bot.min_balance * xrpPrice)} -{" "}
                            {bot.max_balance
                              ? formatCurrency(bot.max_balance * xrpPrice)
                              : "∞"}{" "}
                            USD
                          </p>
                          {bot.stats && (
                            <p className="text-xs text-blue-400 mt-1">
                              Avg:{" "}
                              {formatXRP(bot.stats.average_investment || 0)} XRP
                            </p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-white">
                            {bot.users_count || 0}
                            {bot.max_users > 0 ? `/${bot.max_users}` : ""}
                          </p>
                          <p className="text-xs text-gray-400">
                            {bot.max_users === 0
                              ? "Unlimited slots"
                              : `${bot.max_users - (bot.users_count || 0)} left`}
                          </p>
                          {bot.stats?.total_invested ? (
                            <p className="text-xs text-yellow-400 mt-1">
                              {formatXRP(bot.stats.total_invested)} XRP invested
                            </p>
                          ) : null}
                        </td>
                        <td className="py-3 px-4">
                          {bot.returning_customer_bonus != null &&
                          bot.returning_customer_bonus > 0 ? (
                            <div className="flex items-center gap-1 text-yellow-400">
                              <GiftIcon className="w-3 h-3" />
                              <span className="text-xs font-medium">
                                {formatXRP(bot.returning_customer_bonus)} XRP
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${bot.is_active ? "bg-green-900 text-green-300" : "bg-red-900 text-red-300"}`}
                          >
                            {bot.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-1">
                            <button
                              className="p-2 hover:bg-gray-700 rounded-lg"
                              onClick={() => setShowStatsModal(bot)}
                              title="Stats"
                            >
                              <EyeIcon className="w-4 h-4 text-gray-400" />
                            </button>
                            <button
                              className="p-2 hover:bg-gray-700 rounded-lg"
                              onClick={() => openInvestmentStats(bot)}
                              title="Investment Analytics"
                            >
                              <ChartBarIcon className="w-4 h-4 text-purple-400" />
                            </button>
                            <button
                              className="p-2 hover:bg-gray-700 rounded-lg"
                              onClick={() => openEdit(bot)}
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4 text-blue-400" />
                            </button>
                            <button
                              className="p-2 hover:bg-gray-700 rounded-lg"
                              onClick={() =>
                                handleToggleStatus(bot.id, bot.is_active)
                              }
                              title="Toggle"
                            >
                              {bot.is_active ? (
                                <XCircleIcon className="w-4 h-4 text-red-400" />
                              ) : (
                                <CheckCircleIcon className="w-4 h-4 text-green-400" />
                              )}
                            </button>
                            <button
                              className="p-2 hover:bg-gray-700 rounded-lg"
                              onClick={() => setShowDeleteModal(bot)}
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {pagination.last_page > 1 && (
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Showing{" "}
                {(pagination.current_page - 1) * pagination.per_page + 1}–
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}{" "}
                of {pagination.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => loadBots(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 bg-gray-800 rounded text-sm">
                  Page {pagination.current_page}/{pagination.last_page}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => loadBots(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
      </main>

      <BottomNav />

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Trading Bot"
        size="lg"
      >
        <BotForm />
        <div className="flex space-x-3 mt-6">
          <Button
            variant="tertiary"
            onClick={() => setShowCreateModal(false)}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateBot}
            loading={loading.bots}
            disabled={!formData.name || !formData.min_balance}
            fullWidth
          >
            Create Bot
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!showEditModal}
        onClose={() => setShowEditModal(null)}
        title="Edit Trading Bot"
        size="lg"
      >
        <BotForm isEdit />
        <div className="flex space-x-3 mt-6">
          <Button
            variant="tertiary"
            onClick={() => setShowEditModal(null)}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBot}
            loading={loading.bots}
            disabled={!formData.name}
            fullWidth
          >
            Update Bot
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        title="Delete Trading Bot"
        size="sm"
      >
        {showDeleteModal && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-8 h-8 text-red-300" />
              </div>
              <h3 className={globalStyle.h5}>Delete {showDeleteModal.name}?</h3>
              <p className="text-sm text-gray-400 mt-2">
                This action cannot be undone.
              </p>
            </div>
            {(showDeleteModal.users_count || 0) > 0 && (
              <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-sm text-red-200">
                ⚠️ This bot has {showDeleteModal.users_count} active users.
                Deleting it will end their subscriptions.
              </div>
            )}
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowDeleteModal(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteBot}
                loading={loading.bots}
                fullWidth
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!showStatsModal}
        onClose={() => setShowStatsModal(null)}
        title="Bot Statistics"
        size="md"
      >
        {showStatsModal && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  label: "Active Users",
                  value: showStatsModal.users_count || 0,
                  sub:
                    showStatsModal.max_users > 0
                      ? `Max: ${showStatsModal.max_users}`
                      : "Unlimited",
                },
                {
                  label: "Daily Rate",
                  value: formatNumber(showStatsModal.daily_rate, 4) + "%",
                  sub: `~${formatNumber((Math.pow(1 + showStatsModal.daily_rate / 100, 30) - 1) * 100, 2)}% / 30d`,
                },
                {
                  label: "Investment Range",
                  value: `${formatXRP(showStatsModal.min_balance)} - ${showStatsModal.max_balance ? formatXRP(showStatsModal.max_balance) : "∞"} XRP`,
                  sub: "",
                },
                {
                  label: "Duration",
                  value: showStatsModal.duration_days + " days",
                  sub: showStatsModal.return_capital
                    ? "Capital returned"
                    : "No capital return",
                },
              ].map((s) => (
                <div key={s.label} className="bg-gray-800 rounded-lg p-4">
                  <p className="text-sm text-gray-400">{s.label}</p>
                  <p className="text-xl font-bold text-white">{s.value}</p>
                  {s.sub && <p className="text-xs text-gray-400">{s.sub}</p>}
                </div>
              ))}
            </div>
            {showStatsModal.stats && (
              <div className="bg-gray-800 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-medium text-white mb-2">
                  Investment Stats
                </h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total Invested</span>
                  <span className="text-yellow-400 font-medium">
                    {formatXRP(showStatsModal.stats.total_invested || 0)} XRP
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Avg Investment</span>
                  <span className="text-white">
                    {formatXRP(showStatsModal.stats.average_investment || 0)}{" "}
                    XRP
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Min Investment</span>
                  <span className="text-white">
                    {formatXRP(showStatsModal.stats.min_investment || 0)} XRP
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Max Investment</span>
                  <span className="text-white">
                    {formatXRP(showStatsModal.stats.max_investment || 0)} XRP
                  </span>
                </div>
              </div>
            )}
            <div className="bg-gray-800 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Min Referrals</span>
                <span className="text-white">
                  {showStatsModal.min_referrals}
                </span>
              </div>
              {showStatsModal.returning_customer_bonus != null &&
                showStatsModal.returning_customer_bonus > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">
                      Returning Customer Bonus
                    </span>
                    <span className="text-yellow-400 font-medium flex items-center gap-1">
                      <GiftIcon className="w-3 h-3" />
                      {formatXRP(showStatsModal.returning_customer_bonus)} XRP
                    </span>
                  </div>
                )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Status</span>
                <span
                  className={
                    showStatsModal.is_active ? "text-green-400" : "text-red-400"
                  }
                >
                  {showStatsModal.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <Button
              variant="tertiary"
              onClick={() => setShowStatsModal(null)}
              fullWidth
            >
              Close
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!showInvestmentModal}
        onClose={() => {
          setShowInvestmentModal(null);
          setInvestmentAnalytics(null);
        }}
        title="Investment Analytics"
        size="lg"
      >
        {showInvestmentModal && investmentAnalytics && (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className={globalStyle.h5}>{showInvestmentModal.name}</h3>
              <p className="text-sm text-gray-400">
                Investment Performance Overview
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-400">Total Users</p>
                <p className="text-2xl font-bold text-white">
                  {investmentAnalytics.investment_summary.total_users}
                </p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-400">Total Invested</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {formatXRP(
                    investmentAnalytics.investment_summary.total_invested,
                  )}
                </p>
                <p className="text-xs text-gray-400">XRP</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-400">Avg Investment</p>
                <p className="text-2xl font-bold text-white">
                  {formatXRP(
                    investmentAnalytics.investment_summary.avg_investment,
                  )}
                </p>
                <p className="text-xs text-gray-400">XRP</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-400">Daily Earnings</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatXRP(
                    investmentAnalytics.investment_summary.daily_earnings_total,
                  )}
                </p>
                <p className="text-xs text-gray-400">XRP</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3">
                  Investment Distribution
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Min</span>
                    <span className="text-white font-medium">
                      {formatXRP(
                        investmentAnalytics.investment_summary.min_investment,
                      )}{" "}
                      XRP
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Max</span>
                    <span className="text-white font-medium">
                      {formatXRP(
                        investmentAnalytics.investment_summary.max_investment,
                      )}{" "}
                      XRP
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-700">
                    <span className="text-gray-400">Range Allowed</span>
                    <span className="text-blue-400 font-medium">
                      {investmentAnalytics.bot_settings.min_allowed} -{" "}
                      {investmentAnalytics.bot_settings.max_allowed || "∞"} XRP
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3">Returns</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Daily Rate</span>
                    <span className="text-green-400 font-medium">
                      {investmentAnalytics.bot_settings.daily_rate}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration</span>
                    <span className="text-white font-medium">
                      {investmentAnalytics.bot_settings.duration_days} days
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total ROI</span>
                    <span className="text-green-400 font-medium">
                      {formatNumber(
                        investmentAnalytics.bot_settings.daily_rate *
                          investmentAnalytics.bot_settings.duration_days,
                        2,
                      )}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-700">
                    <span className="text-gray-400">Earnings Generated</span>
                    <span className="text-green-400 font-medium">
                      {formatXRP(
                        investmentAnalytics.investment_summary
                          .total_earnings_generated,
                      )}{" "}
                      XRP
                    </span>
                  </div>
                  {investmentAnalytics.bot_settings.returning_customer_bonus !=
                    null &&
                    investmentAnalytics.bot_settings.returning_customer_bonus >
                      0 && (
                      <div className="flex justify-between pt-2 border-t border-gray-700">
                        <span className="text-gray-400">Returning Bonus</span>
                        <span className="text-yellow-400 font-medium flex items-center gap-1">
                          <GiftIcon className="w-3 h-3" />
                          {formatXRP(
                            investmentAnalytics.bot_settings
                              .returning_customer_bonus,
                          )}{" "}
                          XRP
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div
              className={`rounded-lg p-4 ${showInvestmentModal.return_capital ? "bg-blue-900/20 border border-blue-800/40" : "bg-gray-800"}`}
            >
              <div className="flex items-center gap-2">
                <ShieldCheckIcon
                  className={`w-5 h-5 ${showInvestmentModal.return_capital ? "text-blue-400" : "text-gray-500"}`}
                />
                <span className="text-sm font-medium text-white">
                  Capital Return:{" "}
                  {showInvestmentModal.return_capital ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
            <Button
              variant="tertiary"
              onClick={() => {
                setShowInvestmentModal(null);
                setInvestmentAnalytics(null);
              }}
              fullWidth
            >
              Close
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminTradingBots;
